import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  MapPin,
  Phone,
  ChatText,
  Clock,
  User,
  Briefcase,
  Wrench,
  NotePencil,
  Image as ImageIcon,
  CheckCircle,
  Car,
  Warning,
  Plus,
  CaretRight,
  Copy,
} from "phosphor-react";
import {
  StatusBadge,
  PriorityBadge,
  StatusUpdateModal,
  AddNoteModal,
  CompleteVisitModal,
  TechLoadingState,
} from "../../components/TechComponents";
import { NotesList } from "../../components/NotesPhotos";
import { PhotoGallery, UploadPhotoModal } from "../../components/photo";
import { useTranslation } from "../../hooks/useTranslation";
import VoiceNotesCard from "../../components/VoiceNotesCard";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TechVisitDetail() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const { t, translateToEnglish, translateServiceType, shouldTranslate } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [visit, setVisit] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);

  const fetchVisit = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tech/visits/${visitId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setVisit(data);
      } else {
        toast.error("Failed to load visit");
        navigate("/tech");
      }
    } catch (error) {
      console.error("Visit fetch error:", error);
      toast.error("Failed to load visit");
    } finally {
      setLoading(false);
    }
  }, [visitId, navigate]);

  useEffect(() => {
    fetchVisit();
  }, [fetchVisit]);

  const handleStatusUpdate = (updatedVisit) => {
    setVisit((prev) => ({ ...prev, ...updatedVisit }));
  };

  const handleNoteAdded = () => {
    fetchVisit();
  };

  const handlePhotoAdded = () => {
    fetchVisit();
  };

  const handleComplete = (updatedVisit) => {
    setVisit((prev) => ({ ...prev, ...updatedVisit }));
    toast.success("Visit completed!");
  };

  const openMaps = () => {
    const address = visit?.job?.address || visit?.customer?.addresses?.[0];
    if (address) {
      const query = encodeURIComponent(
        `${address.street || ""}, ${address.city || ""}, ${address.state || ""} ${address.zip || ""}`
      );
      window.open(`https://maps.google.com/?q=${query}`, "_blank");
    }
  };

  const callCustomer = () => {
    if (visit?.customer?.phone) {
      window.location.href = `tel:${visit.customer.phone}`;
    }
  };

  const textCustomer = () => {
    if (visit?.customer?.phone) {
      window.location.href = `sms:${visit.customer.phone}`;
    }
  };

  const copyAddress = () => {
    const address = visit?.job?.address || visit?.customer?.addresses?.[0];
    if (address) {
      const addressStr = `${address.street || ""}, ${address.city || ""}, ${address.state || ""} ${address.zip || ""}`;
      navigator.clipboard.writeText(addressStr);
      toast.success(t("Address copied"));
    }
  };

  if (loading) {
    return <TechLoadingState message={t("Loading visit details...")} />;
  }

  if (!visit) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">{t("Visit not found")}</p>
        <Button onClick={() => navigate("/tech")} className="mt-4">
          {t("Go Back")}
        </Button>
      </div>
    );
  }

  const job = visit.job || {};
  const customer = visit.customer || {};
  const address = job.address || customer.addresses?.[0];
  const isCompleted = visit.status === "completed";

  return (
    <div className="space-y-4 pb-24 lg:pb-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 transition-colors"
      >
        <ArrowLeft size={20} />
        <span className="font-medium">{t("Back")}</span>
      </button>

      {/* Header Card */}
      <Card className="border-2 border-zinc-200">
        <CardContent className="p-4 lg:p-6">
          {/* Status & Priority */}
          <div className="flex items-center gap-2 mb-3">
            <StatusBadge status={visit.status} size="lg" />
            {job.priority && job.priority !== "normal" && (
              <PriorityBadge priority={job.priority} size="lg" />
            )}
          </div>

          {/* Job Title */}
          <h1 className="font-heading text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-2">
            {t(job.title) || t("Untitled Job")}
          </h1>

          {/* Service Type & Time */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500 mb-4">
            <span className="flex items-center gap-1 capitalize">
              <Briefcase size={16} />
              {translateServiceType(job.service_type) || t("General")}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={16} />
              {format(new Date(visit.scheduled_start), "EEE, MMM d • h:mm a")}
              {visit.scheduled_end && ` - ${format(new Date(visit.scheduled_end), "h:mm a")}`}
            </span>
          </div>

          {/* Description */}
          {job.description && (
            <p className="text-zinc-600 text-sm mb-4">{t(job.description)}</p>
          )}

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            {!isCompleted && (
              <>
                {visit.status === "scheduled" && (
                  <Button
                    onClick={() => setStatusModalOpen(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <Car size={18} className="mr-2" />
                    {t("Start En Route")}
                  </Button>
                )}
                {(visit.status === "en_route" || visit.status === "on_site" || visit.status === "in_progress") && (
                  <Button
                    onClick={() => setCompleteModalOpen(true)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    {t("Complete Visit")}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setStatusModalOpen(true)}
                  className="border-2"
                >
                  {t("Update Status")}
                </Button>
              </>
            )}
            {isCompleted && (
              <Badge className="bg-emerald-100 text-emerald-700 px-4 py-2">
                <CheckCircle size={16} className="mr-2" />
                {t("Visit Completed")}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Customer & Location */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <User size={20} />
            {t("Customer & Location")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Customer Info */}
          <div className="p-4 bg-zinc-50 rounded-sm">
            <p className="font-medium text-lg">{customer.name || t("No customer")}</p>
            {customer.company && (
              <p className="text-sm text-zinc-500">{customer.company}</p>
            )}
          </div>

          {/* Contact Buttons */}
          {customer.phone && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={callCustomer}
                className="flex-1 h-12 border-2"
              >
                <Phone size={18} className="mr-2" />
                {t("Call")}
              </Button>
              <Button
                variant="outline"
                onClick={textCustomer}
                className="flex-1 h-12 border-2"
              >
                <ChatText size={18} className="mr-2" />
                {t("Text")}
              </Button>
            </div>
          )}

          {/* Address */}
          {address && (
            <div className="p-4 bg-zinc-50 rounded-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium">{address.street}</p>
                  <p className="text-sm text-zinc-500">
                    {address.city}, {address.state} {address.zip}
                  </p>
                </div>
                <button onClick={copyAddress} className="p-2 hover:bg-zinc-200 rounded-sm">
                  <Copy size={16} className="text-zinc-400" />
                </button>
              </div>
              <Button
                onClick={openMaps}
                className="w-full mt-3 bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                <MapPin size={18} className="mr-2" />
                {t("Open in Maps")}
                <CaretRight size={16} className="ml-auto" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assigned Technicians */}
      {visit.assigned_technicians?.length > 0 && (
        <Card className="border-2 border-zinc-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Wrench size={20} />
              {t("Assigned Technicians")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {visit.assigned_technicians.map((tech) => (
                <div
                  key={tech.technician_id}
                  className="flex items-center gap-3 p-3 bg-zinc-50 rounded-sm"
                >
                  <div className="w-10 h-10 rounded-full bg-[#FF6600]/10 flex items-center justify-center">
                    <Wrench size={20} className="text-[#FF6600]" />
                  </div>
                  <div>
                    <p className="font-medium">{tech.name}</p>
                    <p className="text-xs text-zinc-500">{tech.email}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes & Photos Tabs */}
      <Card className="border-2 border-zinc-200">
        <Tabs defaultValue="notes" className="w-full">
          <CardHeader className="pb-0">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger
                value="notes"
                className="data-[state=active]:bg-[#FF6600] data-[state=active]:text-white"
              >
                <NotePencil size={18} className="mr-2" />
                {t("Notes")} ({visit.notes?.length || 0})
              </TabsTrigger>
              <TabsTrigger
                value="photos"
                className="data-[state=active]:bg-[#FF6600] data-[state=active]:text-white"
              >
                <ImageIcon size={18} className="mr-2" />
                {t("Photos")} ({visit.photos?.length || 0})
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          <TabsContent value="notes" className="p-4">
            <Button
              variant="outline"
              onClick={() => setNoteModalOpen(true)}
              className="w-full mb-4 border-2 border-dashed h-12"
            >
              <Plus size={18} className="mr-2" />
              {t("Add Note")}
            </Button>
            <NotesList
              notes={visit.notes || []}
              onDelete={fetchVisit}
              onUpdate={fetchVisit}
              canEdit={true}
            />
          </TabsContent>

          <TabsContent value="photos" className="p-4">
            <Button
              variant="outline"
              onClick={() => setPhotoModalOpen(true)}
              className="w-full mb-4 border-2 border-dashed h-12"
            >
              <Plus size={18} className="mr-2" />
              {t("Add Photo")}
            </Button>
            <PhotoGallery
              photos={visit.photos || []}
              onDelete={fetchVisit}
              onUpdate={fetchVisit}
              canEdit={true}
            />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Voice Notes Card */}
      <VoiceNotesCard 
        visitId={visitId} 
        onNoteAdded={fetchVisit}
        t={t}
      />

      {/* Job History */}
      {visit.previous_visits?.length > 0 && (
        <Card className="border-2 border-zinc-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Clock size={20} />
              {t("Previous Visits")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {visit.previous_visits.map((pv) => (
                <div
                  key={pv.visit_id}
                  className="flex items-center justify-between p-3 bg-zinc-50 rounded-sm"
                >
                  <div>
                    <p className="font-medium text-sm">
                      {format(new Date(pv.scheduled_start), "MMM d, yyyy")}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {format(new Date(pv.scheduled_start), "h:mm a")}
                    </p>
                  </div>
                  <StatusBadge status={pv.status} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fixed Bottom Action Bar (Mobile) */}
      {!isCompleted && (
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-72 p-4 bg-white border-t-2 border-zinc-200 flex gap-3 z-30">
          {visit.status === "scheduled" && (
            <Button
              onClick={() => setStatusModalOpen(true)}
              className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Car size={20} className="mr-2" />
              {t("I'm On My Way")}
            </Button>
          )}
          {visit.status === "en_route" && (
            <Button
              onClick={() => {
                // Quick update to on_site
                fetch(`${API}/tech/visits/${visitId}/status`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({ status: "on_site" }),
                }).then((res) => {
                  if (res.ok) {
                    res.json().then(handleStatusUpdate);
                    toast.success(t("Arrived on site!"));
                  }
                });
              }}
              className="flex-1 h-12 bg-purple-500 hover:bg-purple-600 text-white"
            >
              <MapPin size={20} className="mr-2" />
              {t("I've Arrived")}
            </Button>
          )}
          {(visit.status === "on_site" || visit.status === "in_progress") && (
            <Button
              onClick={() => setCompleteModalOpen(true)}
              className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <CheckCircle size={20} className="mr-2" />
              {t("Complete Visit")}
            </Button>
          )}
        </div>
      )}

      {/* Modals */}
      <StatusUpdateModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        visit={visit}
        onStatusUpdate={handleStatusUpdate}
      />
      <AddNoteModal
        open={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        visitId={visitId}
        onNoteAdded={handleNoteAdded}
        translateToEnglish={shouldTranslate ? translateToEnglish : null}
      />
      <UploadPhotoModal
        open={photoModalOpen}
        onOpenChange={setPhotoModalOpen}
        visitId={visitId}
        onUploadComplete={handlePhotoAdded}
      />
      <CompleteVisitModal
        open={completeModalOpen}
        onClose={() => setCompleteModalOpen(false)}
        visit={visit}
        onComplete={handleComplete}
      />
    </div>
  );
}
